import { ArrowLeft } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";

const ASSETS = {
  logo: "/logo.png",
};

export default function TermsConditions() {
  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-white/10 p-6 md:p-8">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href="/" className="text-white/60 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </a>
            <img src={ASSETS.logo} alt="Gaffer Athletics" className="h-8" />
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 md:px-8 py-12 md:py-16">
        <h1 className="heading-xl text-3xl md:text-4xl text-white mb-8">Terms & Conditions</h1>
        <p className="text-white/40 text-sm mb-12">Last updated: February 2026</p>

        <div className="space-y-12 body-text text-white/70">
          {/* Introduction */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">1. Introduction</h2>
            <p>
              These Terms & Conditions govern your use of the Gaffer Athletics website and the purchase of products from our online store. By placing an order, you agree to be bound by these terms. Please read them carefully before making a purchase.
            </p>
          </section>

          {/* Company Information */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">2. Company Information</h2>
            <p className="mb-4">
              Gaffer Athletics is operated by:
            </p>
            <ul className="list-none space-y-2 text-white/60">
              <li><strong className="text-white">Company Name:</strong> Gaffer Athletics ApS</li>
              <li><strong className="text-white">Address:</strong> 8870 Langå, Denmark</li>
              <li><strong className="text-white">CVR Number:</strong> 46469550</li>
              <li><strong className="text-white">Email:</strong> contact@gafferathletics.com</li>
            </ul>
          </section>

          {/* Prices and Payment */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">3. Prices and Payment</h2>
            <p className="mb-4">
              All prices displayed on our website are in EUR and include VAT (Value Added Tax) where applicable. Shipping costs are calculated and displayed separately at checkout before you complete your order.
            </p>
            <p className="mb-4">
              We accept the following payment methods:
            </p>
            <ul className="list-disc list-inside space-y-2 text-white/60 ml-4">
              <li>Credit/Debit Cards (Visa, MasterCard)</li>
              <li>Apple Pay</li>
              <li>MobilePay</li>
              <li>PayPal</li>
            </ul>
            <p className="mt-4">
              Payment is processed securely through our payment providers. Your payment information is encrypted and never stored on our servers.
            </p>
          </section>

          {/* Right of Withdrawal */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">4. Right of Withdrawal (14-Day Return Policy)</h2>
            <p className="mb-4">
              In accordance with EU consumer protection laws, you have the right to withdraw from your purchase within 14 days without giving any reason. The withdrawal period expires 14 days from the day on which you acquire physical possession of the goods.
            </p>
            <p className="mb-4">
              To exercise your right of withdrawal, you must inform us of your decision by a clear statement (e.g., a letter sent by post or email). You may use our standard withdrawal form, but it is not obligatory.
            </p>
            <p className="mb-4">
              <strong className="text-white">Effects of withdrawal:</strong> If you withdraw from this contract, we shall reimburse all payments received from you, including the costs of delivery (except for supplementary costs resulting from your choice of a type of delivery other than the least expensive standard delivery offered by us), without undue delay and no later than 14 days from the day on which we are informed of your decision to withdraw.
            </p>
            <p>
              You must return the goods without undue delay and in any event no later than 14 days from the day on which you communicate your withdrawal. The goods must be returned in their original condition and packaging.
            </p>
          </section>

          {/* Warranty and Complaints */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">5. Warranty and Right to Complain (2-Year Guarantee)</h2>
            <p className="mb-4">
              Under Danish and EU consumer law, you have a 2-year right to complain about defects in products purchased from us. This means you can have a defective product repaired, replaced, or receive a refund if the product does not conform to the contract.
            </p>
            <p className="mb-4">
              To make a complaint, please contact us with:
            </p>
            <ul className="list-disc list-inside space-y-2 text-white/60 ml-4">
              <li>Your order number</li>
              <li>A description of the defect</li>
              <li>Photos of the defect (if applicable)</li>
            </ul>
            <p className="mt-4">
              We will assess your complaint and respond within a reasonable timeframe. If the complaint is valid, we will offer repair, replacement, or refund as appropriate.
            </p>
          </section>

          {/* Delivery */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">6. Delivery Terms</h2>
            <p className="mb-4">
              We deliver to most countries within the EU and selected international destinations. Delivery times and costs vary depending on your location:
            </p>
            <ul className="list-disc list-inside space-y-2 text-white/60 ml-4">
              <li><strong className="text-white">Denmark:</strong> 2-4 business days</li>
              <li><strong className="text-white">EU Countries:</strong> 5-10 business days</li>
              <li><strong className="text-white">Rest of World:</strong> 10-20 business days</li>
            </ul>
            <p className="mt-4">
              Please note that delivery times are estimates and may be affected by factors beyond our control. You will receive tracking information once your order has been dispatched.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">7. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law, Gaffer Athletics shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our products or website. Our total liability shall not exceed the amount paid by you for the product in question.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">8. Governing Law</h2>
            <p>
              These Terms & Conditions are governed by Danish law. Any disputes arising from these terms shall be resolved in the Danish courts, without prejudice to your rights under applicable consumer protection legislation in your country of residence.
            </p>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">9. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms & Conditions at any time. Changes will be posted on this page with an updated revision date. Your continued use of our website following any changes constitutes acceptance of the new terms.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">10. Contact Us</h2>
            <p>
              If you have any questions about these Terms & Conditions, please contact us at:
            </p>
            <p className="mt-4 text-white">
              Email: contact@gafferathletics.com
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 p-6 md:p-8">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-white/30 text-sm">
            © 2026 Gaffer Athletics. Engineering for the Sideline.
          </p>
        </div>
      </footer>
    </div>
  );
}
